// Slice #7b — PortfolioGlance: offline-aware mobile read surface over the live
// `mobile_portfolio_glance(_portfolio)` RPC.
//
// Portfolio picker (P1..R5, default P1; last choice held in component state) → useCachedQuery
// (per-portfolio cache key so each portfolio keeps its own last-known-good) → a recharts line of
// series[] (value + benchmark, null-guarded), a period summary grid (1m/3m/6m/YTD/1Yr/3Yrs with
// portfolio/benchmark/excess returns + volatility, numbers-or-null → "—"), the as_at freshness and
// stale/offline hint, and a benchmark_unverified caveat for the R-range. The RPC self-gates: a caller
// without cio/pm/analyst surface access gets a 42501, surfaced inline as a clear no-access state
// (we never crash or blank). Offline → last-known cached data + a "Saved …" affordance.

import { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { cn } from '@/lib/utils';

// ---- RPC payload shape (stale-typed RPC → cast at the call site; this is the runtime contract) ----

type Family = 'P' | 'R';

interface GlanceSummaryRow {
  portfolio_return: number | null;
  benchmark_return: number | null;
  benchmark2_return: number | null;
  excess_return: number | null;
  excess2_return: number | null;
  volatility: number | null;
}

interface GlanceSeriesPoint {
  data_date: string;
  value: number | null;
  benchmark: number | null;
}

interface PortfolioGlance {
  portfolio: string;
  family: Family;
  graph_type: string;
  as_at: string | null;
  benchmark_unverified: boolean;
  summary: Record<string, GlanceSummaryRow | null>;
  series: GlanceSeriesPoint[];
}

// Fixed picker — there is no per-user portfolio list server-side, so expose the full basket.
const PORTFOLIOS = ['P1', 'P2', 'P3', 'P4', 'P5', 'R1', 'R2', 'R3', 'R4', 'R5'] as const;
type PortfolioKey = (typeof PORTFOLIOS)[number];

// Ordered periods exactly as the RPC keys them.
const PERIODS = ['1m', '3m', '6m', 'YTD', '1Yr', '3Yrs'] as const;

const SUMMARY_ROWS: ReadonlyArray<{ key: keyof GlanceSummaryRow; label: string }> = [
  { key: 'portfolio_return', label: 'Portfolio' },
  { key: 'benchmark_return', label: 'Benchmark' },
  { key: 'excess_return', label: 'Excess' },
  { key: 'volatility', label: 'Volatility' },
];

const ACCESS_DENIED = '42501';

// Throw-on-error fetcher. useCachedQuery requires the queryFn to THROW so the cached placeholder is
// served offline (and so the error path stays catchable). We preserve the Postgres error code on the
// thrown Error so the component can distinguish a 42501 (no access) from any other failure.
async function fetchGlance(portfolio: PortfolioKey): Promise<PortfolioGlance> {
  const { data, error } = await (supabase.rpc as any)('mobile_portfolio_glance', {
    _portfolio: portfolio,
  });
  if (error) {
    const e = new Error(error.message ?? 'Failed to load portfolio glance') as Error & {
      code?: string;
    };
    e.code = error.code;
    throw e;
  }
  return data as PortfolioGlance;
}

// Number → percent string (or em-dash for null). Summary values are already %-scaled returns.
function fmtPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v.toFixed(2)}%`;
}

function fmtDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

export default function MobilePortfolioGlance() {
  const online = useIsOnline();
  // Picker state — default P1; the chosen key is also the per-portfolio cache scope.
  const [portfolio, setPortfolio] = useState<PortfolioKey>('P1');

  const glance = useCachedQuery<PortfolioGlance>({
    queryKey: ['mobile-portfolio-glance', portfolio],
    cacheKey: `mobile.portfolio_glance.${portfolio}`,
    queryFn: () => fetchGlance(portfolio),
    // Treat a 42501 (no access) as terminal — retrying won't help.
    retry: (count: number, err: Error) =>
      (err as { code?: string }).code === ACCESS_DENIED ? false : count < 1,
  });

  const data = glance.data;

  // 42501 → inline no-access. Only when we have NO cached data to fall back to (a once-authorized
  // user who later went offline still sees their last-known-good rather than an access wall).
  const accessDenied =
    !data && (glance.error as { code?: string } | null)?.code === ACCESS_DENIED;

  // Stale/offline affordance (mirrors MobileHome's header-line pattern).
  const showStale = glance.isFromCache && glance.cachedAt != null;
  let staleLabel: string | null = null;
  if (showStale && glance.cachedAt) {
    try {
      staleLabel = `Saved ${formatDistanceToNow(new Date(glance.cachedAt), { addSuffix: true })}`;
    } catch {
      staleLabel = 'Showing saved data';
    }
  }

  // Chart rows — keep only points with at least a portfolio value; recharts skips null dataKeys.
  const chartData = useMemo(() => {
    const pts = data?.series ?? [];
    return pts
      .filter((p) => p.value != null || p.benchmark != null)
      .map((p) => ({
        date: fmtDateShort(p.data_date),
        value: p.value,
        benchmark: p.benchmark,
      }));
  }, [data?.series]);

  const hasBenchmarkSeries = useMemo(
    () => (data?.series ?? []).some((p) => p.benchmark != null),
    [data?.series],
  );

  const asAtLabel = useMemo(() => {
    if (!data?.as_at) return null;
    const d = new Date(data.as_at);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
  }, [data?.as_at]);

  return (
    <div className="min-h-screen bg-background px-4 pb-8 pt-[calc(1.25rem+env(safe-area-inset-top))]">
      <header className="mb-4">
        <Link
          to="/partner/m/home"
          className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          Home
        </Link>
        <p className="text-sm text-muted-foreground">Portfolio glance</p>
        <h1 className="font-heading text-2xl font-semibold text-primary">{portfolio}</h1>
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          {asAtLabel && (
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/70">
              As at {asAtLabel}
            </p>
          )}
          {showStale && staleLabel && (
            <p
              className={cn(
                'text-[11px] font-medium uppercase tracking-wide',
                online ? 'text-muted-foreground/70' : 'text-amber-600',
              )}
            >
              {staleLabel}
            </p>
          )}
        </div>
      </header>

      {/* Portfolio picker */}
      <div
        className="mb-4 -mx-4 flex gap-2 overflow-x-auto px-4 pb-1"
        role="group"
        aria-label="Choose portfolio"
      >
        {PORTFOLIOS.map((p) => {
          const active = p === portfolio;
          return (
            <button
              key={p}
              type="button"
              onClick={() => setPortfolio(p)}
              aria-pressed={active}
              className={cn(
                'shrink-0 rounded-full border px-3 py-1 text-sm font-medium transition-colors',
                active
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-muted-foreground',
              )}
            >
              {p}
            </button>
          );
        })}
      </div>

      {accessDenied ? (
        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="font-heading text-sm font-semibold">No access</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            You don&apos;t have access to portfolio analytics. Ask an administrator for CIO, PM or
            analyst access.
          </p>
        </section>
      ) : (
        <div className="space-y-3">
          {/* Trend chart */}
          <section className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-sm font-semibold">1-year trend</h2>
              {hasBenchmarkSeries && (
                <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: 'hsl(var(--primary))' }}
                      aria-hidden="true"
                    />
                    Portfolio
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span
                      className="inline-block h-2 w-2 rounded-full bg-muted-foreground/50"
                      aria-hidden="true"
                    />
                    Benchmark
                  </span>
                </div>
              )}
            </div>
            {chartData.length >= 2 ? (
              <div className="mt-3 h-40 w-full" aria-label="Portfolio value over the last year">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsLineChart
                    data={chartData}
                    margin={{ top: 5, right: 8, left: 0, bottom: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                      minTickGap={24}
                      className="text-xs"
                    />
                    <YAxis
                      tick={{ fontSize: 10 }}
                      width={36}
                      className="text-xs"
                      domain={['auto', 'auto']}
                    />
                    <Tooltip
                      formatter={(value: number) => [
                        value == null ? '—' : value.toLocaleString(),
                        '',
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name="Portfolio"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                    {hasBenchmarkSeries && (
                      <Line
                        type="monotone"
                        dataKey="benchmark"
                        name="Benchmark"
                        stroke="hsl(var(--muted-foreground))"
                        strokeWidth={1.5}
                        strokeDasharray="4 3"
                        dot={false}
                        connectNulls
                        isAnimationActive={false}
                      />
                    )}
                  </RechartsLineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                {glance.isLoading ? 'Loading trend…' : 'No trend data available yet.'}
              </p>
            )}
            {data?.benchmark_unverified && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-amber-600">
                Benchmark unverified
              </p>
            )}
          </section>

          {/* Period summary */}
          <section className="rounded-xl border border-border bg-card p-4">
            <h2 className="font-heading text-sm font-semibold">Period summary</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="text-left">
                    <th className="pb-2 pr-3 font-medium text-muted-foreground" scope="col"> </th>
                    {PERIODS.map((period) => (
                      <th
                        key={period}
                        scope="col"
                        className="px-2 pb-2 text-right font-medium text-muted-foreground"
                      >
                        {period}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SUMMARY_ROWS.map((rowDef) => (
                    <tr key={rowDef.key} className="border-t border-border">
                      <th
                        scope="row"
                        className="py-2 pr-3 text-left font-medium text-muted-foreground"
                      >
                        {rowDef.label}
                      </th>
                      {PERIODS.map((period) => {
                        const cell = data?.summary?.[period];
                        const v = cell ? cell[rowDef.key] : null;
                        const positive = typeof v === 'number' && v > 0;
                        const negative = typeof v === 'number' && v < 0;
                        return (
                          <td
                            key={period}
                            className={cn(
                              'px-2 py-2 text-right font-mono tabular-nums',
                              rowDef.key !== 'volatility' && positive && 'text-green-600',
                              rowDef.key !== 'volatility' && negative && 'text-red-600',
                            )}
                          >
                            {fmtPct(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!data && (
              <p className="mt-2 text-[11px] uppercase tracking-wide text-muted-foreground/70">
                {glance.isLoading ? 'Loading…' : 'No summary data available.'}
              </p>
            )}
          </section>

          {/* Compliance entry point */}
          <Link
            to="/partner/m/compliance"
            className="block rounded-xl border border-border bg-card p-4"
          >
            <h2 className="font-heading text-sm font-semibold">Compliance traffic light</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              RAG status and open breaches across the book.
            </p>
          </Link>
        </div>
      )}
    </div>
  );
}
